<template>
  <b-row>
    <b-col md="4">
      <FormulateInput
        id="tax-parameter-ncm"
        v-model="fiscalTaxParameter.ncm"
        name="tax-parameter-ncm"
        type="select-searchable"
        :label="$t('NCM')"
        :placeholder="$t('Digite para pesquisar')"
        :options="getComboNcms"
        class="required"
        validation="required"
        :disabled="isEdit"
        @fetchSearch="ncmSearch"
      />
    </b-col>
    <b-col md="3">
      <FormulateInput
        id="tax-parameter-st-cbs-ibs"
        v-model="fiscalTaxParameter.stCbsIbsCode"
        name="tax-parameter-st-cbs-ibs"
        type="vue-select"
        :options="comboCstCbsIbs"
        :placeholder="$t('Selecione')"
        class="required"
        validation="required"
        :label="$t('Situação Trib. CBS/IBS')"
      />
    </b-col>
    <b-col md="5">
      <FormulateInput
        id="tax-parameter-fiscal-classification-cbs-ibs"
        v-model="fiscalTaxParameter.fiscalClassificationCbsIbsCode"
        name="tax-parameter-fiscal-classification-cbs-ibs"
        type="vue-select"
        :placeholder="$t('Selecione')"
        class="required"
        validation="required"
        :disabled="!fiscalTaxParameter.stCbsIbsCode"
        :options="comboFiscalClassificationCbsIbs"
        :label="$t('Classificação CBS/IBS')"
      />
    </b-col>
  </b-row>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import { mapGetters, mapState, mapActions } from 'vuex'

export default {
  name: 'FiscalTaxParameterForm',
  components: {
    BRow,
    BCol,
  },

  props: {
    busy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEdit: !!this.$router.currentRoute.params.ncm,
    }
  },

  computed: {
    ...mapState('pages/invoice/fiscalTaxParameters/fiscalTaxParameterMaintain', [
      'fiscalTaxParameter',
    ]),
    ...mapGetters('common/ncms', ['getComboNcms']),
    ...mapGetters('pages/invoice/fiscalTaxParameters', ['getCstCbsIbs', 'getClassTribsByCst']),
    comboCstCbsIbs() {
      return this.getCstCbsIbs.map(c => ({
        value: c.code,
        label: `${c.code} - ${c.description}`,
      }))
    },
    comboFiscalClassificationCbsIbs() {
      const cst = this.fiscalTaxParameter.stCbsIbsCode
      const items = this.getClassTribsByCst(cst)
      return items.map(c => ({ value: c.code, label: `${c.code} - ${c.description}` }))
    },
  },

  watch: {
    'fiscalTaxParameter.stCbsIbsCode': {
      handler: function onFiscalTaxParameterStCbsIbs(val, oldVal) {
        if (val) this.fetchClassTribsByCst(val)

        if (!this.busy && val !== oldVal) {
          this.fiscalTaxParameter.fiscalClassificationCbsIbsCode = null
        }

        try {
          const cstCode = val
          const allCsts = this.getCstCbsIbs || []
          const selected = (allCsts || []).find(x => x.code === cstCode)
          const requiresTaxation = selected && selected.requiresTaxation === true
          this.fiscalTaxParameter.ratesVisible = !!requiresTaxation
        } catch (e) {
          this.fiscalTaxParameter.ratesVisible = true
        }
      },
    },
    'fiscalTaxParameter.fiscalClassificationCbsIbsCode': {
      handler: async function onFiscalClassificationChange(val) {
        if (!val) return
        try {
          const { data } = await this.$http.get(`/api/tax/tax-reform/class-trib/${val}`)
          if (data) {
            this.fiscalTaxParameter.cbsReductionPercent =
              data.cbsReductionPercent ?? this.fiscalTaxParameter.cbsReductionPercent
            if (data.ibsReductionPercent != null) {
              this.fiscalTaxParameter.ibsStateReductionPercent = data.ibsReductionPercent
              this.fiscalTaxParameter.ibsMunicipalReductionPercent = data.ibsReductionPercent
            }
          }
        } catch (e) {
          this.showError({ message: this.$t('Falha ao buscar classificação fiscal') })
        }
        try {
          const cstCode = this.fiscalTaxParameter.stCbsIbsCode
          const allCsts = this.getCstCbsIbs || []
          const selected = (allCsts || []).find(x => x.code === cstCode)
          const requiresTaxation = selected && selected.requiresTaxation === true
          if (!requiresTaxation) {
            this.fiscalTaxParameter.ratesVisible = false
            this.fiscalTaxParameter.cbsPercent = 0
            this.fiscalTaxParameter.cbsReductionPercent = 0
            this.fiscalTaxParameter.cbsDeferralPercent = 0
            this.fiscalTaxParameter.ibsStatePercent = 0
            this.fiscalTaxParameter.ibsStateReductionPercent = 0
            this.fiscalTaxParameter.ibsStateDeferralPercent = 0
            this.fiscalTaxParameter.ibsMunicipalPercent = 0
            this.fiscalTaxParameter.ibsMunicipalReductionPercent = 0
            this.fiscalTaxParameter.ibsMunicipalDeferralPercent = 0
            return
          }
          this.fiscalTaxParameter.ratesVisible = true
        } catch (e) {
          // ignore; default visible
        }
        try {
          const cst = this.fiscalTaxParameter.stCbsIbsCode
          const items = this.getClassTribsByCst(cst) || []
          const found = items.find(x => x.code === val)
          if (found) {
            if (
              this.fiscalTaxParameter.cbsReductionPercent == null ||
              this.fiscalTaxParameter.cbsReductionPercent === 0
            ) {
              if (found.cbsReductionPercent != null)
                this.fiscalTaxParameter.cbsReductionPercent = found.cbsReductionPercent
            }
            const needIbsState =
              this.fiscalTaxParameter.ibsStateReductionPercent == null ||
              this.fiscalTaxParameter.ibsStateReductionPercent === 0
            const needIbsMunicipal =
              this.fiscalTaxParameter.ibsMunicipalReductionPercent == null ||
              this.fiscalTaxParameter.ibsMunicipalReductionPercent === 0
            if ((needIbsState || needIbsMunicipal) && found.ibsReductionPercent != null) {
              if (needIbsState)
                this.fiscalTaxParameter.ibsStateReductionPercent = found.ibsReductionPercent
              if (needIbsMunicipal)
                this.fiscalTaxParameter.ibsMunicipalReductionPercent = found.ibsReductionPercent
            }
          }
        } catch (e) {
          // ignore fallback errors
        }

        try {
          const { data: rates } = await this.$http.get('/api/tax/tax-reform/rates')
          if (rates) {
            this.fiscalTaxParameter.cbsPercent = rates.cbsRatePercent
            this.fiscalTaxParameter.ibsStatePercent =
              rates.effectiveIbsStateRatePercent ?? rates.ibsStateDefaultRatePercent
            this.fiscalTaxParameter.ibsMunicipalPercent =
              rates.effectiveIbsMunicipalRatePercent ?? rates.ibsMunicipalDefaultRatePercent
            this.fiscalTaxParameter.ratesMeta = {
              referenceDate: rates.referenceDate,
              cbsStartDate: rates.cbsStartDate,
              cbsEndDate: rates.cbsEndDate,
              ibsStateStartDate: rates.ibsStateStartDate,
              ibsStateEndDate: rates.ibsStateEndDate,
              ibsMunicipalStartDate: rates.ibsMunicipalStartDate,
              ibsMunicipalEndDate: rates.ibsMunicipalEndDate,
            }
            this.fiscalTaxParameter.ratesLocked = false
          }
        } catch (e) {
          this.showError({ message: this.$t('Falha ao buscar alíquotas vigentes') })
        }
      },
    },
  },

  mounted() {
    this.fetchCombos()
    if (this.fiscalTaxParameter.stCbsIbsCode) {
      this.fetchClassTribsByCst(this.fiscalTaxParameter.stCbsIbsCode)
    }
  },

  methods: {
    ...mapActions('common/ncms', ['fetchNcms']),
    ...mapActions('pages/invoice/fiscalTaxParameters', ['fetchCombos', 'fetchClassTribsByCst']),

    async ncmSearch(loading, search) {
      try {
        loading(true)
        await this.fetchNcms({ itemSearch: search })
      } catch (e) {
        this.showError({
          message: this.$t('Não foi possível fazer a pesquisa'),
        })
      } finally {
        loading(false)
      }
    },
  },
}
</script>
